import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "../utils/axiosConfig";
import "./TCDSForm.css";

const defaultFormData = {
  tcds_type: "",
  tcds_no: "",
  section_number: "",
  section_title: "",
  revision_no: 0,
  customer_name: "",
  purchase_order_no: "",
  po_date: "",
  po_received_date: "",
  project_name: "",
  project_no: "",
  offer_no: "",
  offer_date: "",
  tcds_release_date: "",
  crm_date: "",
  cdd: "",
  ld: "",
  regd_office_address: "",
  automated_valves_qty: 0,
  manual_valves_qty: 0,
  spares_qty: 0,
  product_fg_code: "",
  section_for: "",
  make: "",
  series: "",
  type: "",
  end_connection: "",
  size: "",
  class: "",
  body_material: "",
  disc_material: "",
  stem_material: "",
  seat_material: "",
  operator: "",
  painting_details: "",
  flow_direction: "",
  operation: "",
  air_fail_action: "",
  power_fail_action: "",
  opening_time: "",
  closing_time: "",
  gad: "",
  qap: "",
  special_testing: "",
  certification: "",
  mor: "",
  model: "",
  moc: "",
  customer_item_code: "",
  quantity: 0,
  tag_numbers: [], // Dynamic array of tag numbers based on quantity
  status: "draft",
  // Operating Conditions (from screenshot)
  opr_pressure: "",
  max_pressure: "",
  min_pressure: "",
  max_temp: "",
  min_temp: "",
  // Related Automation Package
  related_automation_pkg: "",
  // Notes
  notes: "",
};

function TCDSForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [formData, setFormData] = useState(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: "", message: "" });
  const [valves, setValves] = useState([]);
  const [selectedValve, setSelectedValve] = useState("");
  const [valveParameters, setValveParameters] = useState([]);
  const [valvesLoading, setValvesLoading] = useState(true);
  const [dynamicParams, setDynamicParams] = useState({});
  const [sizeOptions, setSizeOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [specialTestingOptions, setSpecialTestingOptions] = useState([]);
  const [operationParameters, setOperationParameters] = useState({});
  
  // Multi-accessories state — each entry: { uid, accessoryId, accessoryName, model, models, parameters }
  const [addedAccessories, setAddedAccessories] = useState([]);
  const [accessoriesList, setAccessoriesList] = useState([]); // full list from API
  const [accessoriesLoading, setAccessoriesLoading] = useState(true);
  
  // Tag numbers state
  const [tagNumbers, setTagNumbers] = useState([]);
  
  // State to track if valve type was loaded from edit mode
  const [valveTypeLoaded, setValveTypeLoaded] = useState(false);
  
  const editMode = !!id;

  useEffect(() => {
    if (location.state?.tcdsType) {
      setFormData(prev => ({ ...prev, tcds_type: location.state.tcdsType }));
    }
    if (location.state?.unitId) {
      setFormData(prev => ({ ...prev, unit_id: location.state.unitId }));
    }
    if (editMode) loadTCDS();
    
    // Load both valves and accessories
    fetchValves();
    fetchAccessories();
    fetchSpecialTestingOptions();
    fetchOperationParameters();
  }, [id, location.state]);

  // Effect to handle valve type loading after valves are fetched in edit mode
  useEffect(() => {
    // Only run when:
    // 1. Not in edit mode OR valveTypeLoaded is already true -> skip
    // 2. Valves are still loading -> skip
    // 3. valves array is empty -> skip
    if (!editMode || valveTypeLoaded || valvesLoading || valves.length === 0) {
      return;
    }

    // valves just finished loading and we have data.valve_type_id from loadTCDS
    // The loadTCDS function already sets selectedValve via loadValveConfiguration
    // So we just need to mark as loaded
    setValveTypeLoaded(true);
  }, [valves, valvesLoading, editMode, valveTypeLoaded]);

  const fetchValves = async () => {
    try {
      setValvesLoading(true);
      const response = await axios.get("/valve-types");
      const valveData = response.data.data || response.data || [];
      setValves(valveData);
    } catch (error) {
      console.error("Failed to fetch valves:", error);
    } finally {
      setValvesLoading(false);
    }
  };

  const fetchAccessories = async () => {
    try {
      setAccessoriesLoading(true);
      const response = await axios.get("/accessories");
      const accessoryData = response.data.data || response.data || [];
      setAccessoriesList(accessoryData);
    } catch (error) {
      console.error("Failed to fetch accessories:", error);
    } finally {
      setAccessoriesLoading(false);
    }
  };

  const fetchSpecialTestingOptions = async () => {
    try {
      const response = await axios.get("/standards/special_testing");
      const data = response.data.data || [];
      setSpecialTestingOptions(data.map(item => item.value));
    } catch (error) {
      console.error("Failed to fetch special testing options:", error);
      setSpecialTestingOptions([]);
    }
  };

  const fetchOperationParameters = async () => {
    try {
      const response = await axios.get("/operation-parameters-with-values");
      const params = response.data.data || [];

      // Convert array to object for easier access
      const paramMap = {};
      params.forEach(param => {
        // Filter out null values from JSON_ARRAYAGG
        const values = (param.values || []).filter(v => v !== null);
        paramMap[param.name] = {
          id: param.id,
          name: param.name,
          type: param.type,
          values: values || []
        };
      });

      setOperationParameters(paramMap);
    } catch (error) {
      console.error("Failed to fetch operation parameters:", error);
      setOperationParameters({});
    }
  };

  const normalizeForMatch = (value) => String(value || "").trim().toLowerCase();

  const findMatchingValveTypeId = async (savedData) => {
    try {
      const response = await axios.get("/api-configurations/active");
      const configs = response.data.data || [];

      const keysToMatch = ["make", "series", "type", "end_connection", "size", "class"];
      
      // First check if there's ANY valve-related data saved
      // If all valve fields are empty, return empty (no valve selected)
      const hasAnyValveData = keysToMatch.some(key => savedData[key] && String(savedData[key]).trim() !== "");
      if (!hasAnyValveData) {
        return ""; // No valve was selected - don't auto-match
      }
      
      const isMatch = (config) => keysToMatch.every((key) => {
        const savedValue = savedData[key];
        if (!savedValue) return true;
        return normalizeForMatch(config[key]) === normalizeForMatch(savedValue);
      });

      const matched = configs.find(isMatch);
      return matched?.valve_type_id ? String(matched.valve_type_id) : "";
    } catch (error) {
      console.warn("Failed to infer valve type from saved data:", error);
      return "";
    }
  };

  const loadValveConfiguration = async (
    valveId,
    {
      applyAutofill = true,
      preserveDynamicParams = false,
      existingDynamicParams = {},
      showToast = true,
    } = {}
  ) => {
    if (!valveId) return;

    const response = await axios.get(`/valve-types/${valveId}/with-parameters`);
    const valveData = response.data.data;

    setSelectedValve(String(valveId));

    // Extract Class and Size parameter values from valve parameters
    const parameters = valveData.parameters || [];
    setValveParameters(parameters);

    // Find Class and Size parameters and their values
    const classParam = parameters.find(p => p.name.toLowerCase() === 'class');
    const sizeParam = parameters.find(p => p.name.toLowerCase() === 'size');
    
    setSizeOptions(sizeParam?.values || []);
    setClassOptions(classParam?.values || []);

    if (applyAutofill) {
      setFormData((prev) => ({
        ...prev,
        make: valveData.make || "",
        series: valveData.series || "",
        // IMPORTANT: Use valve type NAME as primary source for type field (e.g., "Globe Stop", "Wedge Gate")
        // This ensures TCDS records have proper valve type for MTC filtering
        type: valveData.name || valveData.type || "",
        end_connection: valveData.end_connection || "",
        size: valveData.size || "",
        class: valveData.class || "",
        body_material: valveData.body_material || valveData.body || "",
        disc_material: valveData.disc_material || valveData.disc || "",
        stem_material: valveData.stem_material || valveData.stem || "",
        seat_material: valveData.seat_material || valveData.seat || "",
        operator: valveData.operator || "",
        // DON'T auto-fill operation parameters - let user select from dropdowns
        painting_details: prev.painting_details || "",
        flow_direction: prev.flow_direction || "",
        operation: prev.operation || "",
        air_fail_action: prev.air_fail_action || "",
        power_fail_action: prev.power_fail_action || "",
        opening_time: prev.opening_time || "",
        closing_time: prev.closing_time || "",
        gad: prev.gad || "",
        qap: prev.qap || "",
      }));
    }

    const initialParams = {};
    parameters.forEach((param) => {
      // Use case-insensitive lookup for dynamic parameters to handle name variations
      const savedValue = Object.keys(existingDynamicParams).find(
        key => key.toLowerCase() === param.name.toLowerCase()
      );
      const value = savedValue ? existingDynamicParams[savedValue] : undefined;

      initialParams[param.name] = preserveDynamicParams
        ? (value ?? param.default_value ?? "")
        : (param.default_value || "");
    });
    setDynamicParams(initialParams);

    if (showToast) {
      if (parameters.length > 0) {
        showNotification("success", `Loaded ${parameters.length} valve parameters successfully`);
      } else {
        showNotification("warning", "No parameters configured for this valve type");
      }
    }
  };

  // ── Multi-accessory helpers ──

  const isAccessorySelectedElsewhere = (accessoryId, currentRowUid) => {
    if (!accessoryId) return false;
    return addedAccessories.some(
      (row) => row.uid !== currentRowUid && String(row.accessoryId) === String(accessoryId)
    );
  };

  const addAccessoryRow = () => {
    setAddedAccessories(prev => [
      ...prev,
      { uid: Date.now(), accessoryId: "", accessoryName: "", model: "", models: [], parameters: [], parameterValues: {} },
    ]);
  };

  const removeAccessoryRow = (uid) => {
    setAddedAccessories(prev => prev.filter(a => a.uid !== uid));
  };

  const handleAccessoryRowChange = async (uid, accessoryId) => {
    const accessory = accessoriesList.find(a => String(a.id) === String(accessoryId));
    setAddedAccessories(prev =>
      prev.map(a => a.uid === uid ? {
        ...a,
        accessoryId,
        accessoryName: accessory?.name || "",
        model: "",
        models: [],
        parameters: [],
        parameterValues: {}
      } : a)
    );
    if (!accessoryId) return;
    try {
      // Fetch configurations (models) and parameters in one call
      const configRes = await axios.get(`/accessories/${accessoryId}/configurations`);

      const configurations = configRes.data.data?.configurations || [];
      const models = [...new Set(configurations.map(c => c.model))].filter(Boolean);

      // Get parameters from the response
      const parameters = configRes.data.data?.parameters || [];

      // For Dropdown-type parameters, fetch their allowed values in parallel
      const dropdownParams = parameters.filter(p => p.input_type === "Dropdown");
      const dropdownValueResponses = await Promise.all(
        dropdownParams.map(p =>
          axios.get(`/accessories/parameters/${p.id}/values`).catch(() => ({ data: { data: [] } }))
        )
      );

      // Attach dropdown_values to each parameter
      const dropdownValuesMap = {};
      dropdownParams.forEach((p, idx) => {
        const vals = dropdownValueResponses[idx]?.data?.data || [];
        dropdownValuesMap[p.id] = vals.map(v => v.value_text);
      });

      const enrichedParameters = parameters.map(p => ({
        ...p,
        dropdown_values: p.input_type === "Dropdown" ? (dropdownValuesMap[p.id] || []) : [],
      }));

      // Initialize parameterValues with empty values
      const parameterValues = {};
      enrichedParameters.forEach(p => {
        parameterValues[p.id] = "";
      });

      setAddedAccessories(prev =>
        prev.map(a => a.uid === uid ? {
          ...a,
          models,
          parameters: enrichedParameters,
          parameterValues
        } : a)
      );
    } catch (error) {
      console.error("Failed to load accessory models and parameters:", error);
    }
  };

  const handleAccessoryModelChange = async (uid, model) => {
    setAddedAccessories(prev =>
      prev.map(a => a.uid === uid ? { ...a, model, parameterValues: {} } : a)
    );
    const row = addedAccessories.find(a => a.uid === uid);
    if (!model || !row?.accessoryId) return;
    try {
      const response = await axios.get(`/accessories/${row.accessoryId}/autofill`, { params: { model } });
      const data = response.data.data;

      // Handle both old flat structure and new structured format
      let parameterValues = {};
      if (data.parameters && Array.isArray(data.parameters)) {
        // New structured format
        data.parameters.forEach(param => {
          const paramId = row.parameters.find(p => p.name === param.name)?.id;
          if (paramId) {
            parameterValues[paramId] = param.value;
          }
        });
      } else {
        // Old flat format - map property names to parameter IDs
        Object.entries(data).forEach(([key, val]) => {
          if (key !== 'model') {
            const param = row.parameters.find(p => p.name === key);
            if (param) {
              parameterValues[param.id] = val;
            }
          }
        });
      }

      setAddedAccessories(prev =>
        prev.map(a => a.uid === uid ? { ...a, parameterValues } : a)
      );
    } catch (error) {
      console.error("Failed to load accessory parameters:", error);
    }
  };

  const handleAccessoryParameterChange = async (uid, paramId, newValue) => {
    // Build the updated values map that includes the current change
    const row = addedAccessories.find(a => a.uid === uid);
    const updatedValues = { ...(row?.parameterValues || {}), [paramId]: newValue };

    // Commit the change to state
    setAddedAccessories(prev =>
      prev.map(a => a.uid === uid ? {
        ...a,
        parameterValues: updatedValues,
        // Clear auto-matched model when user manually changes a param
        model: "",
      } : a)
    );

    // Auto-match model: check if ALL parameters are now filled using updatedValues
    if (row && row.parameters && row.parameters.length > 0) {
      const allFilled = row.parameters.every(p => {
        const val = p.id === paramId ? newValue : (row.parameterValues[p.id] ?? "");
        return val !== null && val !== undefined && val.toString().trim() !== "";
      });

      if (allFilled) {
        try {
          const response = await axios.get(`/accessories/${row.accessoryId}/configurations`);
          const configurations = response.data.data?.configurations || [];

          let matchedModel = "";
          for (const config of configurations) {
            let matches = true;
            for (const param of row.parameters) {
              const savedValue = String(config.values[param.id] ?? "").trim();
              const currentValue = String(
                param.id === paramId ? newValue : (row.parameterValues[param.id] ?? "")
              ).trim();
              if (savedValue !== currentValue) {
                matches = false;
                break;
              }
            }
            if (matches) {
              matchedModel = config.model;
              break;
            }
          }

          // Update model (set to matched or clear if no match)
          setAddedAccessories(prev =>
            prev.map(a => a.uid === uid ? { ...a, model: matchedModel } : a)
          );
        } catch (error) {
          console.error("Failed to find matching model:", error);
        }
      }
    }
  };

  const handleValveSelection = async (e) => {
    const valveId = e.target.value;
    setValveParameters([]);
    setDynamicParams({});

    if (!valveId) return;

    try {
      await loadValveConfiguration(valveId, {
        applyAutofill: true,
        preserveDynamicParams: false,
        showToast: true,
      });
    } catch (error) {
      console.error("Failed to load valve data:", error);
      showNotification("error", error.response?.data?.message || "Failed to load valve specifications");
    }
  };

  const loadTCDS = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/tcds/${id}`);
      const data = response.data.data;

      // Ensure date fields are properly formatted for input type="date"
      const dateFields = ['po_date', 'po_received_date', 'offer_date', 'tcds_release_date', 'crm_date', 'cdd'];
      const processedData = { ...data };
      dateFields.forEach(field => {
        if (processedData[field] === null || processedData[field] === undefined) {
          processedData[field] = '';
        }
      });

      // Ensure notes and operating conditions are empty strings instead of null/undefined
      const operatingFields = ['notes', 'opr_pressure', 'max_pressure', 'min_pressure', 'max_temp', 'min_temp', 'related_automation_pkg'];
      operatingFields.forEach(field => {
        if (processedData[field] === null || processedData[field] === undefined) {
          processedData[field] = '';
        }
      });

      setFormData(processedData);

      let parsedDynamicParams = {};

      // Restore dynamic parameters
      if (data.dynamic_parameters) {
        parsedDynamicParams = typeof data.dynamic_parameters === "string"
          ? JSON.parse(data.dynamic_parameters)
          : data.dynamic_parameters;
        setDynamicParams(parsedDynamicParams || {});
      }

      // Restore saved accessories
      if (data.accessories_data) {
        const saved = typeof data.accessories_data === "string"
          ? JSON.parse(data.accessories_data)
          : data.accessories_data;

        if (Array.isArray(saved)) {
          const restoredRows = saved.map((a) => ({
            models: [],
            parameters: [],
            parameterValues: {},
            ...a,
            uid: Date.now() + Math.random(),
          }));

          setAddedAccessories(restoredRows);

          // Rehydrate model options, dropdown values and parameter values so the
          // edit form looks exactly as it was when last saved.
          const hydratedRows = await Promise.all(
            restoredRows.map(async (row) => {
              if (!row.accessoryId) return row;

              try {
                // Single call — same endpoint used by handleAccessoryRowChange
                const configRes = await axios.get(`/accessories/${row.accessoryId}/configurations`);
                const configurations = configRes.data.data?.configurations || [];
                const models       = [...new Set(configurations.map((c) => c.model))].filter(Boolean);
                const freshParams  = configRes.data.data?.parameters || [];

                // Fetch dropdown option lists in parallel (same logic as handleAccessoryRowChange)
                const dropdownParams = freshParams.filter((p) => p.input_type === "Dropdown");
                const dropdownResponses = await Promise.all(
                  dropdownParams.map((p) =>
                    axios
                      .get(`/accessories/parameters/${p.id}/values`)
                      .catch(() => ({ data: { data: [] } }))
                  )
                );
                const dropdownValuesMap = {};
                dropdownParams.forEach((p, idx) => {
                  const vals = dropdownResponses[idx]?.data?.data || [];
                  dropdownValuesMap[p.id] = vals.map((v) => v.value_text);
                });

                // Attach dropdown_values to each fresh parameter definition
                const parameters = freshParams.map((p) => ({
                  ...p,
                  dropdown_values:
                    p.input_type === "Dropdown" ? (dropdownValuesMap[p.id] || []) : [],
                }));

                // ── Build parameterValues (id → saved value) ──────────────────
                // Layer 1: start from any parameterValues already on the saved row
                // (covers the case where the row was saved with the old flat format)
                const parameterValues = { ...(row.parameterValues || {}) };

                // Layer 2: saved parameters array carries .value (written by Fix-1).
                // These always take precedence over Layer 1.
                if (Array.isArray(row.parameters)) {
                  row.parameters.forEach((p) => {
                    if (p.value != null && String(p.value).trim() !== "") {
                      parameterValues[p.id] = String(p.value);
                    }
                  });
                }

                return {
                  ...row,
                  models,
                  parameters: parameters,
                  parameterValues
                };
              } catch (error) {
                console.warn("[TCDSForm] Failed to hydrate accessory:", row.accessoryName, error);
                return row;
              }
            })
          );

          setAddedAccessories(hydratedRows);
        }
      }
      
      // Restore tag numbers
      if (data.tag_numbers) {
        const savedTagNumbers = typeof data.tag_numbers === "string"
          ? JSON.parse(data.tag_numbers)
          : data.tag_numbers;
        if (Array.isArray(savedTagNumbers)) {
          setTagNumbers(savedTagNumbers);
        }
      }

      // Load valve type - first try direct valve_type_id, then fall back to inference
      const savedValveTypeId = data.valve_type_id ? String(data.valve_type_id) : null;
      if (savedValveTypeId) {
        await loadValveConfiguration(savedValveTypeId, {
          applyAutofill: false,
          preserveDynamicParams: true,
          existingDynamicParams: parsedDynamicParams || {},
          showToast: false,
        });
      } else {
        // Infer valve type from saved API config fields so edit form keeps valve config/values.
        const inferredValveTypeId = await findMatchingValveTypeId(data);
        if (inferredValveTypeId) {
          await loadValveConfiguration(inferredValveTypeId, {
            applyAutofill: false,
            preserveDynamicParams: true,
            existingDynamicParams: parsedDynamicParams || {},
            showToast: false,
          });
        }
      }
    } catch (error) {
      showNotification("error", error.response?.data?.message || "Failed to load TCDS");
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: "", message: "" }), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    // Special handling for quantity field
    if (name === "quantity") {
      const qty = parseInt(value) || 0;
      
      // Validate quantity is a positive number
      if (qty < 0) {
        showNotification("error", "Quantity must be a positive number");
        return;
      }
      
      // Update quantity in formData
      setFormData(prev => ({ ...prev, quantity: qty }));
      
      // Dynamically create tag number array
      const newTagNumbers = [];
      for (let i = 0; i < qty; i++) {
        newTagNumbers.push(tagNumbers[i] || ""); // Preserve existing values or empty string
      }
      setTagNumbers(newTagNumbers);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === "number" ? parseInt(value) || 0 : value,
      }));
    }
  };

  const handleDynamicParamChange = (e) => {
    const { name, value } = e.target;
    setDynamicParams(prev => ({ ...prev, [name]: value }));
  };

  const handleTagNumberChange = (index, value) => {
    const newTagNumbers = [...tagNumbers];
    newTagNumbers[index] = value;
    setTagNumbers(newTagNumbers);
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    if (!formData.tcds_no || !formData.customer_name || !formData.purchase_order_no) {
      showNotification("error", "Please fill in required fields: TCDS No., Customer Name and Purchase Order No.");
      return;
    }
    
    // Validate quantity is a positive number
    if (formData.quantity !== undefined && formData.quantity !== null) {
      const qty = parseInt(formData.quantity);
      if (isNaN(qty) || qty < 0) {
        showNotification("error", "Quantity must be a positive number");
        return;
      }
    }
    
    try {
      setLoading(true);
      
      // Sanitize ld field - it's VARCHAR, NOT a date field
      let sanitizedLd = formData.ld || null;
      if (sanitizedLd) {
        // If it looks like an ISO date string, clear it (user error - LD should be text like "0.5% per week")
        if (typeof sanitizedLd === 'string' && sanitizedLd.includes('T') && sanitizedLd.includes('Z')) {
          console.warn("LD field contains date value (expected text like '0.5% per week'), clearing it:", sanitizedLd);
          sanitizedLd = null;  // Clear invalid date values
        } else {
          sanitizedLd = String(sanitizedLd);
        }
      }
      
      const dataToSubmit = {
        ...formData,
        ld: sanitizedLd,
        dynamic_parameters: dynamicParams,
        accessories_data: addedAccessories.map(({ uid, models, parameterValues, parameters, ...rest }) => ({
          ...rest,
          // Embed each parameter's user-entered value so the Excel generator can read it back.
          // parameterValues is keyed by parameter id; we attach the value onto each parameter object.
          parameters: (parameters || []).map(p => ({
            ...p,
            value: parameterValues?.[p.id] ?? p.value ?? "",
          })),
        })),
        tag_numbers: tagNumbers.filter(tag => tag.trim() !== ""), // Only include non-empty tag numbers
        status: saveAsDraft ? "draft" : "submitted",
        valve_type_id: selectedValve || null,
      };

      console.log("Submitting TCDS data:", { ...dataToSubmit, ld: dataToSubmit.ld });

      let response;
      if (editMode) {
        response = await axios.put(`/tcds/${id}`, dataToSubmit);
      } else {
        response = await axios.post("/tcds", dataToSubmit);
      }

      showNotification("success", response.data.message || "TCDS saved successfully");
      
      // Save document header data for future new TCDS forms
      const headerFields = [
        "customer_name", "purchase_order_no", "po_date", "po_received_date",
        "project_name", "project_no", "offer_no", "offer_date",
        "tcds_release_date", "crm_date", "cdd", "ld",
        "regd_office_address", "automated_valves_qty", "manual_valves_qty",
        "spares_qty", "section_for", "product_fg_code"
      ];
      const headerDataToSave = {};
      headerFields.forEach(field => {
        if (formData[field] !== undefined) {
          headerDataToSave[field] = formData[field];
        }
      });
      console.log("Saving header data to localStorage:", headerDataToSave);
      localStorage.setItem("tcds_document_header", JSON.stringify(headerDataToSave));
      
      setTimeout(() => {
        navigate("/manage-tcds");
      }, 1500);
    } catch (error) {
      console.error("Submit error:", error);
      showNotification("error", error.response?.data?.message || "Failed to save TCDS");
    } finally {
      setLoading(false);
    }
  };

  const renderDynamicParam = (param) => {
    const inputName = param.name;
    const value = dynamicParams[inputName] || "";
    
    // Normalize input_type to lowercase for case-insensitive comparison
    const inputType = param.input_type ? param.input_type.toLowerCase() : 'text';

    // Debug rendering
    console.log(`Rendering parameter: ${param.name}`, {
      input_type: param.input_type,
      normalized_type: inputType,
      has_values: !!param.values,
      values_length: param.values?.length || 0
    });

    // Dropdown field - if parameter has values array with items
    if (inputType === "dropdown" && param.values && param.values.length > 0) {
      console.log(`  Rendering dropdown for ${param.name} with ${param.values.length} options`);
      return (
        <select
          className="form-select"
          name={inputName}
          value={value}
          onChange={handleDynamicParamChange}
        >
          <option value="">-- Select {param.name} --</option>
          {param.values.map((v, i) => {
            // Handle both string values and object values {label, value}
            const optionValue = typeof v === 'object' ? (v.value || v.label || v) : v;
            const optionLabel = typeof v === 'object' ? (v.label || v.value || v) : v;
            return (
              <option key={i} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </select>
      );
    } 
    // Number field
    else if (inputType === "number") {
      console.log(`  Rendering number input for ${param.name}`);
      return (
        <input
          className="form-input"
          type="number"
          name={inputName}
          value={value}
          onChange={handleDynamicParamChange}
          placeholder={param.placeholder || `Enter ${param.name}`}
        />
      );
    } 
    // Text field (default)
    else {
      console.log(`  Rendering text input for ${param.name}`);
      return (
        <input
          className="form-input"
          type="text"
          name={inputName}
          value={value}
          onChange={handleDynamicParamChange}
          placeholder={param.placeholder || `Enter ${param.name}`}
        />
      );
    }
  };

  return (
    <div className="tcds-form-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-icon">
            {notification.type === "success" ? "✓" : "✕"}
          </div>
          <div className="notification-text">{notification.message}</div>
        </div>
      )}

      {/* Page Header */}
      <div className="tcds-form-header">
        <div>
          <h1 className="tcds-form-title">
            {editMode ? "Edit TCDS" : "Create New TCDS"}
            {editMode && formData.revision_no !== undefined && (
              <span style={{
                marginLeft: '12px',
                fontSize: '14px',
                fontWeight: 600,
                background: '#dbeafe',
                color: '#1e40af',
                borderRadius: '12px',
                padding: '2px 10px',
                verticalAlign: 'middle',
              }}>
                Rev. {formData.revision_no}
              </span>
            )}
          </h1>
          <p className="tcds-form-subtitle">
            {formData.tcds_type ? `Type: ${formData.tcds_type}` : "Technical Compliance Data Sheet"}
          </p>
        </div>
        <div className="tcds-form-actions">
          <button
            type="button"
            className="btn-back"
            onClick={() => navigate("/manage-tcds")}
          >
            ← Back
          </button>
        </div>
      </div>

      <form className="tcds-form-card" onSubmit={(e) => handleSubmit(e, false)}>

        {/* ══════════════════════════════════════════
            DOCUMENT HEADER — matches spec image layout
            ══════════════════════════════════════════ */}
        <div className="doc-header-section">
          <div className="doc-header-title">TCDS DOCUMENT HEADER</div>

          {/* Row 0: TCDS No. | Rev. No. */}
          <div className="doc-header-row">
            <div className="doc-header-cell">
              <span className="doc-label">TCDS NO.: <span className="required-mark">*</span></span>
              <input
                className="doc-input"
                type="text"
                name="tcds_no"
                value={formData.tcds_no}
                onChange={handleInputChange}
                placeholder="e.g. TCDS-001"
                required
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">REV. NO.:</span>
              <input
                className="doc-input"
                type="number"
                name="revision_no"
                value={formData.revision_no || 0}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
            </div>
          </div>

          {/* Row 1: Customer (full width) */}
          <div className="doc-header-row">
            <div className="doc-header-cell doc-full-width">
              <span className="doc-label">CUSTOMER:</span>
              <input
                className="doc-input"
                type="text"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleInputChange}
                placeholder="Gardner Denver Engineered Products India Private Limited"
                required
              />
            </div>
          </div>

          {/* Row 2: PO No | PO Date | PO Received Date */}
          <div className="doc-header-row">
            <div className="doc-header-cell">
              <span className="doc-label">PURCHASE ORDER NO.: <span className="required-mark">*</span></span>
              <input
                className="doc-input"
                type="text"
                name="purchase_order_no"
                value={formData.purchase_order_no}
                onChange={handleInputChange}
                placeholder="e.g. 12630065"
                required
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">PO DATE:</span>
              <input
                className="doc-input"
                type="date"
                name="po_date"
                value={formData.po_date}
                onChange={handleInputChange}
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">PO / LOI RECEIVED DATE:</span>
              <input
                className="doc-input"
                type="date"
                name="po_received_date"
                value={formData.po_received_date}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Row 3: Project Name | Project No */}
          <div className="doc-header-row">
            <div className="doc-header-cell doc-wide-cell">
              <span className="doc-label">PROJECT NAME:</span>
              <input
                className="doc-input"
                type="text"
                name="project_name"
                value={formData.project_name}
                onChange={handleInputChange}
                placeholder="Project Name"
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">PROJECT NO.:</span>
              <input
                className="doc-input"
                type="text"
                name="project_no"
                value={formData.project_no}
                onChange={handleInputChange}
                placeholder="Project Number"
              />
            </div>
          </div>

          {/* Row 4: Offer No | Offer Date */}
          <div className="doc-header-row">
            <div className="doc-header-cell doc-wide-cell">
              <span className="doc-label">OFFER NO.:</span>
              <input
                className="doc-input"
                type="text"
                name="offer_no"
                value={formData.offer_no}
                onChange={handleInputChange}
                placeholder="e.g. Offer/Automation/25-26/1265"
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">OFFER DATE:</span>
              <input
                className="doc-input"
                type="date"
                name="offer_date"
                value={formData.offer_date}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Row 5: TCDS Release Date | CRM Date | CDD | LD */}
          <div className="doc-header-row">
            <div className="doc-header-cell">
              <span className="doc-label">TCDS RELEASE DATE:</span>
              <input
                className="doc-input"
                type="date"
                name="tcds_release_date"
                value={formData.tcds_release_date}
                onChange={handleInputChange}
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">CRM DATE:</span>
              <input
                className="doc-input"
                type="date"
                name="crm_date"
                value={formData.crm_date}
                onChange={handleInputChange}
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">CDD:</span>
              <input
                className="doc-input"
                type="date"
                name="cdd"
                value={formData.cdd}
                onChange={handleInputChange}
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">LD:</span>
              <input
                className="doc-input"
                type="text"
                name="ld"
                value={formData.ld}
                onChange={handleInputChange}
                placeholder="e.g. 0.5% per week"
              />
            </div>
          </div>

          {/* Row 6: Regd. Office Address (full width) */}
          <div className="doc-header-row">
            <div className="doc-header-cell doc-full-width">
              <span className="doc-label">REGD. OFFICE ADDRESS:</span>
              <textarea
                className="doc-input doc-textarea"
                name="regd_office_address"
                value={formData.regd_office_address}
                onChange={handleInputChange}
                placeholder="Gate No. 182, 183 & 184, Aland-Markal Road, Fulgaon, Pune, Maharashtra, India, ZIP 412216"
                rows={2}
              />
            </div>
          </div>

          {/* Row 7: Qty fields | Section For */}
          <div className="doc-header-row">
            <div className="doc-header-cell">
              <span className="doc-label">AUTOMATED VALVES QTY:</span>
              <input
                className="doc-input"
                type="number"
                name="automated_valves_qty"
                value={formData.automated_valves_qty}
                onChange={handleInputChange}
                min="0"
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">MANUAL VALVES QTY:</span>
              <input
                className="doc-input"
                type="number"
                name="manual_valves_qty"
                value={formData.manual_valves_qty}
                onChange={handleInputChange}
                min="0"
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">SPARES QTY:</span>
              <input
                className="doc-input"
                type="number"
                name="spares_qty"
                value={formData.spares_qty}
                onChange={handleInputChange}
                min="0"
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">SECTION FOR:</span>
              <input
                className="doc-input"
                type="text"
                name="section_for"
                value={formData.section_for}
                onChange={handleInputChange}
                placeholder=""
              />
            </div>
          </div>

          {/* Row 8: Product FG Code | Section Number | Section Title */}
          <div className="doc-header-row">
            <div className="doc-header-cell doc-wide-cell">
              <span className="doc-label">PRODUCT FG CODE:</span>
              <input
                className="doc-input"
                type="text"
                name="product_fg_code"
                value={formData.product_fg_code}
                onChange={handleInputChange}
                placeholder="B506000000000000000000000"
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">SECTION NUMBER:</span>
              <input
                className="doc-input"
                type="text"
                name="section_number"
                value={formData.section_number}
                onChange={handleInputChange}
                placeholder="e.g. SECTION-01"
              />
            </div>
            <div className="doc-header-cell">
              <span className="doc-label">SECTION TITLE:</span>
              <input
                className="doc-input"
                type="text"
                name="section_title"
                value={formData.section_title}
                onChange={handleInputChange}
                placeholder="e.g. BUTTERFLY VALVE F17-F27"
              />
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            VALVE TYPE & ACCESSORY SELECTION
            ══════════════════════════════════════════ */}
        <div className="form-section">
          <div className="section-header">
            <h2 className="section-title">Valve Type & Accessory Selection</h2>
          </div>
          
          {/* VALVE TYPE SELECTION */}
          <div className="form-group">
            <label className="form-label">
              Select Valve Type — parameters will appear below after selection
            </label>
            <select
              className="form-select valve-select"
              value={selectedValve}
              onChange={handleValveSelection}
              disabled={valvesLoading}
            >
              <option value="">
                {valvesLoading ? "Loading valves..." : valves.length === 0 ? "No valves configured" : "-- Select a Valve Type --"}
              </option>
              {valves.map((valve) => (
                <option key={valve.id} value={valve.id}>
                  {valve.name || `${valve.make || ""} ${valve.series || ""} ${valve.type || ""} ${valve.size || ""}`.trim() || `Valve ID: ${valve.id}`}
                </option>
              ))}
            </select>
          </div>

          {/* ACCESSORY SELECTION — Multiple */}
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label className="form-label">Accessories (Optional — Add multiple)</label>
              <button
                type="button"
                className="btn-save-draft"
                style={{ padding: '6px 14px', fontSize: '13px' }}
                onClick={addAccessoryRow}
                disabled={accessoriesLoading}
              >
                + Add Accessory
              </button>
            </div>

            {addedAccessories.length === 0 && (
              <p style={{ color: '#9ca3af', fontSize: '13px', marginTop: '4px' }}>
                {accessoriesLoading ? "Loading accessories..." : "No accessories added yet. Click \"+ Add Accessory\" to add one."}
              </p>
            )}

            {addedAccessories.map((row, idx) => (
              <div key={row.uid} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '14px 16px',
                marginBottom: '10px',
                background: '#f9fafb',
              }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', paddingTop: '8px', minWidth: '20px' }}>#{idx + 1}</span>
                  <div style={{ flex: 1 }}>
                    <div className="form-grid two-column">
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Accessory</label>
                        <select
                          className="form-select"
                          value={row.accessoryId}
                          onChange={(e) => handleAccessoryRowChange(row.uid, e.target.value)}
                        >
                          <option value="">-- Select Accessory --</option>
                          {accessoriesList.map((acc) => {
                            const alreadySelected = isAccessorySelectedElsewhere(acc.id, row.uid);
                            const accessoryLabel = acc.name || `Accessory #${acc.id}`;

                            return (
                              <option
                                key={acc.id}
                                value={acc.id}
                                style={alreadySelected ? { color: "#15803d", fontWeight: 600 } : undefined}
                              >
                                {alreadySelected ? `${accessoryLabel} - Accessory already added ` : accessoryLabel}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Model</label>
                        <select
                          className="form-select"
                          value={row.model}
                          onChange={(e) => handleAccessoryModelChange(row.uid, e.target.value)}
                          disabled={!row.accessoryId || (row.models || []).length === 0}
                        >
                          <option value="">-- Select Model --</option>
                          {(row.models || []).map((m, i) => (
                            <option key={i} value={m}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Parameters for this accessory - shown immediately on selection */}
                    {row.accessoryId && (row.parameters || []).length > 0 && (
                      <div style={{ marginTop: '10px' }}>
                        <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px', fontWeight: 600 }}>
                          ACCESSORY PARAMETERS {row.model && `(Model: ${row.model})`}
                        </p>
                        <div className="form-grid three-column">
                          {row.parameters.map((param) => (
                            <div className="form-group" key={param.id} style={{ marginBottom: 0 }}>
                              <label className="form-label">
                                {param.name}
                                {param.input_type === "Dropdown" && <span style={{ fontSize: '10px', color: '#9ca3af' }}> (dropdown)</span>}
                              </label>
                              {param.input_type === "Dropdown" && param.dropdown_values && param.dropdown_values.length > 0 ? (
                                <select
                                  className="form-select"
                                  value={row.parameterValues?.[param.id] || ""}
                                  onChange={(e) => {
                                    handleAccessoryParameterChange(row.uid, param.id, e.target.value);
                                  }}
                                >
                                  <option value="">-- Select {param.name} --</option>
                                  {param.dropdown_values.map((val, i) => (
                                    <option key={i} value={val}>{val}</option>
                                  ))}
                                </select>
                              ) : param.input_type === "Dropdown" ? (
                                // Dropdown type but no values configured yet — show text input as fallback
                                <input
                                  type="text"
                                  className="form-input"
                                  value={row.parameterValues?.[param.id] || ""}
                                  onChange={(e) => handleAccessoryParameterChange(row.uid, param.id, e.target.value)}
                                  placeholder={`Enter ${param.name}`}
                                />
                              ) : param.input_type === "Number" ? (
                                <input
                                  type="number"
                                  step="any"
                                  className="form-input"
                                  value={row.parameterValues?.[param.id] || ""}
                                  onChange={(e) => handleAccessoryParameterChange(row.uid, param.id, e.target.value)}
                                  placeholder={`Enter ${param.name}`}
                                />
                              ) : (
                                <input
                                  type="text"
                                  className="form-input"
                                  value={row.parameterValues?.[param.id] || ""}
                                  onChange={(e) => handleAccessoryParameterChange(row.uid, param.id, e.target.value)}
                                  placeholder={`Enter ${param.name}`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAccessoryRow(row.uid)}
                    style={{
                      background: '#fee2e2', color: '#991b1b', border: 'none',
                      borderRadius: '6px', padding: '6px 10px', cursor: 'pointer',
                      fontSize: '12px', fontWeight: 600, marginTop: '22px', whiteSpace: 'nowrap',
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Valve-Specific Parameters - shown after selecting valve type */}
        {selectedValve && valveParameters.length > 0 && (
          <div className="form-section">
            <div className="section-header">
              <h2 className="section-title">Valve-Specific Parameters</h2>
              <span className="section-note">Parameters configured for the selected valve type</span>
            </div>
            <div className="form-grid three-column">
              {valveParameters.map((param, idx) => (
                <div className="form-group" key={idx}>
                  <label className="form-label">{param.name?.toUpperCase()}</label>
                  {renderDynamicParam(param)}
                </div>
              ))}
            </div>
          </div>
        )}

        <>
            <div className="form-section">
              <div className="section-header">
                <h2 className="section-title">Operation Parameters</h2>
              </div>
              <div className="form-grid three-column">
                <div className="form-group">
                  <label className="form-label">PAINTING DETAILS</label>
                  {operationParameters.PAINTING_DETAILS && operationParameters.PAINTING_DETAILS.values?.length > 0 ? (
                    <select className="form-input" name="painting_details" value={formData.painting_details} onChange={handleInputChange}>
                      <option value="">-- Select Painting Details --</option>
                      {operationParameters.PAINTING_DETAILS.values.map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                  ) : (
                    <select className="form-input" name="painting_details" value={formData.painting_details} onChange={handleInputChange}>
                      <option value="">-- Loading options --</option>
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">FLOW DIRECTION</label>
                  {operationParameters.FLOW_DIRECTION && operationParameters.FLOW_DIRECTION.values?.length > 0 ? (
                    <select className="form-input" name="flow_direction" value={formData.flow_direction} onChange={handleInputChange}>
                      <option value="">-- Select Flow Direction --</option>
                      {operationParameters.FLOW_DIRECTION.values.map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                  ) : (
                    <select className="form-input" name="flow_direction" value={formData.flow_direction} onChange={handleInputChange}>
                      <option value="">-- Loading options --</option>
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">OPERATION</label>
                  {operationParameters.OPERATION && operationParameters.OPERATION.values?.length > 0 ? (
                    <select className="form-input" name="operation" value={formData.operation} onChange={handleInputChange}>
                      <option value="">-- Select Operation --</option>
                      {operationParameters.OPERATION.values.map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                  ) : (
                    <select className="form-input" name="operation" value={formData.operation} onChange={handleInputChange}>
                      <option value="">-- Loading options --</option>
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">AIR FAIL ACTION</label>
                  {operationParameters.AIR_FAIL_ACTION && operationParameters.AIR_FAIL_ACTION.values?.length > 0 ? (
                    <select className="form-input" name="air_fail_action" value={formData.air_fail_action} onChange={handleInputChange}>
                      <option value="">-- Select Air Fail Action --</option>
                      {operationParameters.AIR_FAIL_ACTION.values.map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                  ) : (
                    <select className="form-input" name="air_fail_action" value={formData.air_fail_action} onChange={handleInputChange}>
                      <option value="">-- Loading options --</option>
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">POWER FAIL ACTION</label>
                  {operationParameters.POWER_FAIL_ACTION && operationParameters.POWER_FAIL_ACTION.values?.length > 0 ? (
                    <select className="form-input" name="power_fail_action" value={formData.power_fail_action} onChange={handleInputChange}>
                      <option value="">-- Select Power Fail Action --</option>
                      {operationParameters.POWER_FAIL_ACTION.values.map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                  ) : (
                    <select className="form-input" name="power_fail_action" value={formData.power_fail_action} onChange={handleInputChange}>
                      <option value="">-- Loading options --</option>
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">OPENING TIME</label>
                  <input
                    className="form-input"
                    type="text"
                    name="opening_time"
                    value={formData.opening_time}
                    onChange={handleInputChange}
                    placeholder="e.g. STD, 5 sec"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">CLOSING TIME</label>
                  <input
                    className="form-input"
                    type="text"
                    name="closing_time"
                    value={formData.closing_time}
                    onChange={handleInputChange}
                    placeholder="e.g. STD, 5 sec"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">GAD</label>
                  {operationParameters.GAD && operationParameters.GAD.values?.length > 0 ? (
                    <select className="form-input" name="gad" value={formData.gad} onChange={handleInputChange}>
                      <option value="">-- Select GAD --</option>
                      {operationParameters.GAD.values.map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                  ) : (
                    <select className="form-input" name="gad" value={formData.gad} onChange={handleInputChange}>
                      <option value="">-- Loading options --</option>
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">QAP</label>
                  {operationParameters.QAP && operationParameters.QAP.values?.length > 0 ? (
                    <select className="form-input" name="qap" value={formData.qap} onChange={handleInputChange}>
                      <option value="">-- Select QAP --</option>
                      {operationParameters.QAP.values.map((val) => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                  ) : (
                    <select className="form-input" name="qap" value={formData.qap} onChange={handleInputChange}>
                      <option value="">-- Loading options --</option>
                    </select>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">QUANTITY <span className="required-mark">*</span></label>
                  <input
                    className="form-input"
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="e.g. 2"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">CUSTOMER ITEM CODE</label>
                  <input
                    className="form-input"
                    type="text"
                    name="customer_item_code"
                    value={formData.customer_item_code}
                    onChange={handleInputChange}
                    placeholder="e.g. 216000000001"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">SPECIAL TESTING</label>
                  <select
                    className="form-input"
                    name="special_testing"
                    value={formData.special_testing}
                    onChange={handleInputChange}
                  >
                    <option value="">Select or enter special testing</option>
                    {specialTestingOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">MAX. PRESS.</label>
                  <input
                    className="form-input"
                    type="text"
                    name="max_pressure"
                    value={formData.max_pressure}
                    onChange={handleInputChange}
                    placeholder="e.g. 15 bar"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">MIN. PRESS.</label>
                  <input
                    className="form-input"
                    type="text"
                    name="min_pressure"
                    value={formData.min_pressure}
                    onChange={handleInputChange}
                    placeholder="e.g. 0 bar"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">MAX. TEMP.</label>
                  <input
                    className="form-input"
                    type="text"
                    name="max_temp"
                    value={formData.max_temp}
                    onChange={handleInputChange}
                    placeholder="e.g. 200°C"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">MIN. TEMP.</label>
                  <input
                    className="form-input"
                    type="text"
                    name="min_temp"
                    value={formData.min_temp}
                    onChange={handleInputChange}
                    placeholder="e.g. -20°C"
                  />
                </div>
                <div className="form-group full-width">
                  <label className="form-label">NOTES</label>
                  <textarea
                    className="form-textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Enter any additional notes..."
                    rows="4"
                  />
                </div>
              </div>
            </div>

            {/* Tag Numbers Table Section */}
            <div className="form-section">
              <span className="section-note">
                {formData.quantity > 0 ? `${formData.quantity} tag number(s) based on quantity` : 'Set quantity above to add tag numbers'}
              </span>
              
              {formData.quantity > 0 && (
                <div className="tag-numbers-table-container">
                  <table className="tag-numbers-table">
                    <thead>
                      <tr>
                        <th style={{ width: '80px' }}>Sr. No.</th>
                        <th>Tag No.</th>
                        <th style={{ width: '80px' }}>Sr. No.</th>
                        <th>Tag No.</th>
                        <th style={{ width: '80px' }}>Sr. No.</th>
                        <th>Tag No.</th>
                        <th style={{ width: '80px' }}>Sr. No.</th>
                        <th>Tag No.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Create rows with 4 tag number pairs each (like in screenshot)
                        const rows = [];
                        const totalTags = formData.quantity;
                        const rowCount = Math.ceil(totalTags / 4);
                        
                        for (let row = 0; row < rowCount; row++) {
                          const rowCells = [];
                          for (let col = 0; col < 4; col++) {
                            const tagIndex = row * 4 + col;
                            if (tagIndex < totalTags) {
                              rowCells.push(
                                <td key={`sr-${tagIndex}`} className="sr-no-cell">{tagIndex + 1}</td>,
                                <td key={`tag-${tagIndex}`}>
                                  <input
                                    className="tag-input"
                                    type="text"
                                    value={tagNumbers[tagIndex] || ""}
                                    onChange={(e) => handleTagNumberChange(tagIndex, e.target.value)}
                                    placeholder={`Tag ${tagIndex + 1}`}
                                  />
                                </td>
                              );
                            } else {
                              rowCells.push(
                                <td key={`sr-empty-${tagIndex}`} className="sr-no-cell empty-cell"></td>,
                                <td key={`tag-empty-${tagIndex}`} className="empty-cell"></td>
                              );
                            }
                          }
                          rows.push(<tr key={`row-${row}`}>{rowCells}</tr>);
                        }
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
              
              {formData.quantity === 0 && (
                <div className="no-tags-message">
                  <p>Please enter a quantity in the Operation Parameters section above to generate tag number fields.</p>
                </div>
              )}
            </div>

            </>

            {/* ══════════════════════════════════════════
            ACCESSORY PARAMETERS — removed (now inline per row)
            ══════════════════════════════════════════ */}

        {/* If no selection made yet, show a prompt */}
        {!selectedValve && addedAccessories.length === 0 && (
          <div className="valve-prompt">
            <div className="valve-prompt-icon">⬆</div>
            <p>Please select a <strong>Valve Type</strong> or <strong>Accessory</strong> above to load its parameters and complete the TCDS form.</p>
          </div>
        )}

        {/* Form Actions - Show if at least valve or accessory is selected */}
        {(selectedValve || addedAccessories.some(a => a.model)) && (
          <div className="form-step-navigation">
            <button
              type="button"
              className="btn-previous"
              onClick={() => navigate("/manage-tcds")}
            >
              ← Cancel
            </button>
            <button
              type="button"
              className="btn-save-draft"
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
            >
              Update TCDS
            </button>
            <button
              type="submit"
              className="btn-submit-bottom"
              disabled={loading}
            >
              {editMode ? "Generate New Revision" : "Submit TCDS"}
            </button>
          </div>
        )}

      </form>
    </div>
  );
}

export default TCDSForm;
